<template>
    <div class="email-form">
        <form-wrapper @submit="handleFormSubmission(data)">
            <common-content-spacer height="tiny">
                <form-row>
                    <form-cell>
                        <common-page-header :heading="headerText" size="large"></common-page-header>
                    </form-cell>
                </form-row>
                <form-row>
                    <form-cell>
                        <label class="form-field__label">{{ $t('forms.email_form.form_labels.to') }}</label>
                        <div
                            v-clickaway="closeToDropdown"
                            class="to-input-wrapper"
                            :class="{ focused: isToFocused }"
                            @click="openToDropdown"
                        >
                            <div class="chips">
                                <span v-for="user in selectedUsers" :key="user.id" class="chip" @click.stop="unselectUser(user)">
                                    {{ user.label }}
                                    <common-icon name="close" size="tiny" class="remove"></common-icon>
                                </span>
                            </div>
                            <input
                                v-model="data.to"
                                type="text"
                                class="to-input"
                                :placeholder="selectedUsers.length ? '' : toPlaceholder"
                                @input="filterToItems($event.target.value)"
                                @focus="openToDropdown"
                            />
                        </div>

                        <common-dropdown :recalculatePosition="true" :isOpen="showSelectToData" :hasArrow="false">
                            <div class="network-users">
                                <div
                                    v-for="(user, index) in filteredItems"
                                    :key="index"
                                    :class="[
                                        'network-user',
                                        { 'network-user-selected': user.user ? selectedItems[user.user.id] : false },
                                    ]"
                                    @click="selectUser(user.user)"
                                >
                                    <div>
                                        <common-user-avatar
                                            size="user-menu"
                                            :user="user.user || user.practice"
                                            :isUserProfileLinkEnabled="false"
                                        ></common-user-avatar>
                                    </div>

                                    <div>
                                        <p>
                                            {{ user.user ? user.user.firstName + ' ' + user.user.lastName : user.practice.name }}
                                        </p>
                                        <p v-if="user.user">{{ user.user.specialities[0].speciality.name }}</p>
                                    </div>
                                </div>

                                <div v-if="showLoading">
                                    <p class="search-network-members__message">
                                        {{ $t('modals.search_network_members.loading_message') }}
                                    </p>
                                </div>

                                <div v-else-if="filteredItems.length === 0">
                                    <p class="search-network-members__message">
                                        {{ $t('modals.search_network_members.no_member_found') }}
                                    </p>
                                </div>
                            </div>
                        </common-dropdown>
                    </form-cell>
                </form-row>

                <form-row v-if="showCcField">
                    <form-cell>
                        <text-field
                            :label="$t('forms.email_form.form_labels.cc')"
                            :placeholder="ccPlaceholder"
                            :formData="data"
                            valuePath="cc"
                            @change="change($event)"
                        ></text-field>
                        <common-labels
                            v-if="selectedEmailsForCcField.length"
                            :labels="selectedEmailsForCcField"
                            @click="removeCcEmail($event)"
                        ></common-labels>
                    </form-cell>
                    <form-cell class="email-form__open-search">
                        <common-button
                            class="open-search-network-button"
                            :isDisabled="false"
                            :isSubmittable="false"
                            :icon="showSelectCcData ? 'close' : 'plus-circle'"
                            size="micro"
                            type="tertiary"
                            @click="toggleSelectCcModal"
                        ></common-button>

                        <common-dropdown
                            v-clickaway="toggleSelectCcModal"
                            :recalculatePosition="true"
                            :isOpen="showSelectCcData"
                            :hasArrow="true"
                            bgColor="white"
                            arrowPositionY="top"
                            arrowPositionX="right"
                        >
                            <search-network-members
                                :selectedUsers="selectedUsers"
                                :items="filteredItems"
                                :showLoading="showLoading"
                                class="search-members-modal"
                                :placeholder="$t('forms.email_form.placeholder.search')"
                                icon="search"
                                iconPosition="right"
                                iconBackground="primary"
                                @input-change="filterItems"
                                @user-selected="addCcEmail"
                            ></search-network-members>
                        </common-dropdown>
                    </form-cell>
                </form-row>

                <form-row v-if="showBccField">
                    <form-cell>
                        <text-field
                            :label="$t('forms.email_form.form_labels.cc')"
                            :placeholder="ccPlaceholder"
                            :formData="data"
                            valuePath="bcc"
                            @change="change($event)"
                        ></text-field>
                        <common-labels
                            v-if="selectedEmailsForBccField.length"
                            :labels="selectedEmailsForBccField"
                            @click="removeBccEmail($event)"
                        ></common-labels>
                    </form-cell>
                    <form-cell class="email-form__open-search">
                        <common-button
                            class="open-search-network-button"
                            :isDisabled="false"
                            :isSubmittable="false"
                            :icon="showSelectCcData ? 'close' : 'plus-circle'"
                            size="micro"
                            type="tertiary"
                            @click="toggleSelectBccModal"
                        ></common-button>

                        <common-dropdown
                            v-clickaway="toggleSelectBccModal"
                            :recalculatePosition="true"
                            :isOpen="showSelectBccData"
                            :hasArrow="true"
                            bgColor="white"
                            arrowPositionY="top"
                            arrowPositionX="right"
                        >
                            <search-network-members
                                :items="filteredItems"
                                :showLoading="showLoading"
                                :selectedUsers="selectedUsers"
                                class="search-members-modal"
                                :placeholder="$t('forms.email_form.placeholder.search')"
                                icon="search"
                                iconPosition="right"
                                iconBackground="primary"
                                @user-selected="addBccEmail"
                                @input-change="filterItems"
                            ></search-network-members>
                        </common-dropdown>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.email_form.form_labels.subject')"
                            :placeholder="$t('forms.email_form.form_labels.subject')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="subject"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.email_form.form_labels.from')"
                            :placeholder="$t('forms.email_form.form_labels.from')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="from"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-editor-field
                            :label="$t('forms.email_form.form_labels.message')"
                            :placeholder="$t('forms.email_form.placeholder.message')"
                            :isRequired="true"
                            :uploadedFiles="[]"
                            :formData="data"
                            :attachmentHintText="$t('forms.email_form.cursor_message.attach_button')"
                            valuePath="message"
                            @change="change($event)"
                        ></text-editor-field>
                    </form-cell>
                </form-row>
                <common-buttons>
                    <common-button
                        :isProcessing="isSending"
                        :isDisabled="isSendDisabled"
                        :label="$t('forms.terms_of_use.support.buttons.send')"
                        type="primary"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
        </form-wrapper>
    </div>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import SearchNetworkMembers from 'components/page-elements/connections/SearchNetworkMembers'
    import TextEditorField from '../../form-fields/TextEditorField.vue'
    import UsersAPI from 'api/UsersAPI'
    import PracticeTypeEnum from 'enums/PracticeTypeEnum'

    export default {
        mixins: [FormMixin],
        components: { SearchNetworkMembers, TextEditorField },
        props: {
            headerText: { type: String, default: 'Email Blast' },
            defaultUser: { type: Object, default: null },
            showOpenSelectMemberModalButton: { type: Boolean, default: false },
            showCcField: { type: Boolean, default: false },
            showBccField: { type: Boolean, default: false },
            showBccWarning: { type: Boolean, default: false },
        },
        data() {
            return {
                isSending: false,
                showSelectToData: false,
                showSelectCcData: false,
                showSelectBccData: false,
                selectedUsers: [],
                selectedEmailsForCcField: [],
                selectedEmailsForBccField: [],
                items: [],
                showLoading: false,
                filteredItems: [],
                selectedItems: {},
                isToFocused: false,
            }
        },
        computed: {
            toPlaceholder() {
                return !this.selectedUsers.length ? this.$t('forms.email_form.placeholder.to') : ''
            },
            ccPlaceholder() {
                return !this.selectedEmailsForCcField.length ? this.$t('forms.email_form.form_labels.cc') : ''
            },
            isSendDisabled() {
                return !(this.data.subject && this.data.message && this.data.from && this.selectedUsers.length)
            },
            defaultRecipientEmail() {
                return this.defaultUser?.email || this.defaultUser?.user?.email || this.defaultUser?.practice?.email || null
            },
        },
        methods: {
            prefillRecipientFromDefaultUser() {
                if (!this.defaultRecipientEmail) return
                const alreadySelected = this.selectedUsers.some((u) => u.label === this.defaultRecipientEmail)
                if (alreadySelected) return

                this.selectedUsers.push({
                    id: this.defaultUser?.id || this.defaultUser?.user?.id || this.defaultRecipientEmail,
                    label: this.defaultRecipientEmail,
                })
            },
            filterToItems(value) {
                const term = value.toLowerCase().trim()
                if (!term) {
                    this.filteredItems = this.items
                    return
                }

                this.filteredItems = this.items.filter((item) => {
                    if (item.user) {
                        const first = item.user.firstName.toLowerCase()
                        const last = item.user.lastName.toLowerCase()
                        const email = item.user.email.toLowerCase()
                        return first.includes(term) || last.includes(term) || email.includes(term)
                    } else {
                        return item.practice?.name.toLowerCase().includes(term)
                    }
                })

                this.showSelectToData = true
            },
            openToDropdown() {
                this.showSelectToData = true
                this.isToFocused = true
            },
            closeToDropdown() {
                this.showSelectToData = false
                this.isToFocused = false
            },
            closeSelectMemberModal() {
                this.showSelectToData = false
            },
            openSelectMemberModal() {
                if (this.showOpenSelectMemberModalButton) {
                    this.showSelectToData = true
                }
            },
            toggleSelectMemberModal() {
                this.showSelectToData = !this.showSelectToData
            },
            toggleSelectCcModal() {
                this.showSelectCcData = !this.showSelectCcData
            },
            toggleSelectBccModal() {
                this.showSelectBccData = !this.showSelectBccData
            },
            handleFormSubmission({ subject, message, from }) {
                let to = []
                let cc = []
                let bcc = []
                this.selectedUsers.forEach((u) => {
                    to.push(u.label)
                })
                this.selectedEmailsForCcField.forEach((user) => {
                    cc.push(user.label)
                })
                this.selectedEmailsForBccField.forEach((user) => {
                    bcc.push(user.label)
                })
                const formData = {
                    to: to,
                    cc: cc,
                    bcc: bcc,
                    subject: subject,
                    message: message,
                    from: from,
                }
                this.emitEvent({
                    name: 'email-data',
                    payload: formData,
                })
                this.clearFormData()
            },

            clearFormData() {
                this.data.message = null
                this.data.subject = null
                this.data.cc = null
                this.selectedUsers = []
                this.selectedEmailsForCcField = []
                this.selectedEmailsForBccField = []
            },
            unselectUser(ev) {
                this.selectedUsers.forEach((user, index) => {
                    if (user.id === ev.id) {
                        this.selectedUsers.splice(index, 1)
                    }
                })
            },
            selectUser(selected) {
                if (!selected) return

                const index = this.selectedUsers.findIndex((u) => u.id === selected.id)

                if (index === -1) {
                    this.$set(this.selectedUsers, this.selectedUsers.length, {
                        id: selected.id,
                        label: selected.email,
                    })
                } else {
                    this.selectedUsers.splice(index, 1)
                }

                this.data.to = ''

                this.filteredItems = this.items

                this.showSelectToData = true
            },

            removeCcEmail(ev) {
                this.selectedEmailsForCcField.forEach((user, index) => {
                    if (user.label === ev.label) {
                        this.selectedEmailsForCcField.splice(index, 1)
                    }
                })
            },
            removeBccEmail(ev) {
                this.selectedEmailsForBccField.forEach((user, index) => {
                    if (user.label === ev.label) {
                        this.selectedEmailsForBccField.splice(index, 1)
                    }
                })
            },
            addCcEmail(user) {
                let exist = false
                this.selectedEmailsForCcField.forEach((u, index) => {
                    if (u.id === user.id) {
                        this.selectedEmailsForCcField.splice(index, 1)
                        exist = true
                    }
                })
                if (!exist) {
                    this.$set(this.selectedEmailsForCcField, this.selectedEmailsForCcField.length, {
                        id: user.id,
                        label: user.email,
                    })
                }
            },
            addBccEmail(user) {
                let exist = false
                this.selectedEmailsForBccField.forEach((u, index) => {
                    if (u.id === user.id) {
                        this.selectedEmailsForBccField.splice(index, 1)
                        exist = true
                    }
                })
                if (!exist) {
                    this.$set(this.selectedEmailsForBccField, this.selectedEmailsForBccField.length, {
                        id: user.id,
                        label: user.email,
                    })
                }
            },

            getAllUsers() {
                this.showLoading = true
                const request = {
                    fields: [
                        'user(id,email,firstName,lastName,designation,physicianGroup(name),specialities(speciality(id,name)),avatar(thumb-360,source))',
                        'practice(id,practiceType,name)',
                    ],
                    practiceType: this.$route.query.practiceType || PracticeTypeEnum.PHYSICIAN,
                }

                UsersAPI.getMyConnectionsFromProviderTable(request, this.$auth.getActiveMember().id)
                    .then((resp) => {
                        this.items = resp
                        this.filteredItems = resp
                    })
                    .finally(() => {
                        this.showLoading = false
                    })
            },

            filterItems({ searchTerm }) {
                if (searchTerm.length > 0) {
                    const term = searchTerm.toLowerCase().trim()
                    this.filteredItems = this.items.filter((item) => {
                        return item.user
                            ? item.user?.firstName.toLowerCase().includes(term) ||
                                  item.user?.lastName.toLowerCase().includes(term)
                            : item.practice?.name.toLowerCase().includes(term)
                    })
                } else {
                    this.filteredItems = this.items
                }
            },
        },
        mounted() {
            const { firstName, lastName, email } = this.$auth.getActiveMember()
            this.$set(this.data, 'from', firstName + ' ' + lastName + ' - ' + email)
            this.prefillRecipientFromDefaultUser()
            this.getAllUsers()
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .network-user {
        display: flex;
        padding: 10px;
        cursor: pointer;
    }
    .network-user > div:last-child {
        @include breakpoint(smartphone-large) {
            width: 200px;
        }
        width: 300px;
        margin-left: 15px;
    }
    .network-user > div:first-child {
        margin-left: 30px;
    }
    .network-user p:first-child {
        font-weight: 500;
    }
    .network-user p:last-child {
        font-weight: 200;
    }
    .network-user:hover {
        background-color: #f5fbfe;
    }
    .network-user-selected {
        background-color: #e8f7ff;
    }
    .network-users {
        overflow-y: scroll;
        max-height: 210px;
    }
    .search-network-users__search-field {
        margin-left: 30px;
        margin-top: 30px;
        margin-right: 30px;
    }
    .search-network-users__search-field > div:first-child {
        margin-right: 30px;
    }
    .search-network-members__message {
        margin: 30px;
        text-align: center;
    }
    .to-input-wrapper {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        min-height: 50px;
        padding: 15px 10px;
        border: 1px solid #ccc;
        border-radius: 4px;
        cursor: text;
        transition: border-color 0.2s ease;
    }

    .to-input-wrapper.focused {
        border-color: $color--accent-1;
    }
    .to-input-wrapper .chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    .to-input::placeholder {
        color: #b9b9b9 !important;
        opacity: 1;
    }

    .chip {
        display: flex;
        align-items: center;
        padding: 4px 8px;
        background: $color--accent-1;
        border-radius: 12px;
        font-size: 13px;
        color: #fff;
    }

    .chip .remove {
        margin-left: 10px;
        cursor: pointer;
        font-weight: bold;
    }

    .to-input {
        border: none;
        outline: none;
        flex: 1 1 150px;
        width: 100%;
        min-width: 120px;
        font-size: 14px;
        margin-left: 8px;
        color: $color--txt-label;
    }
</style>
